import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { sanitizeCurrencyInput } from "../lib/utils";
import { CaixaRegistradoraIcon, MesaIcon, MotoEntregaIcon } from "@/components/icons/ModulosIcons";
import { FiltroTipoCliente, ORDEM_TIPOS, ParaQuemVale, TIPOS_CLIENTE, nomesTipos, ofertaPassa, tipoDaUrl } from "@/features/tipos-cliente";
import { useSearchParams } from "react-router-dom";
import {
  Gift, Plus, Pencil, Trash2, X, Search, Percent, DollarSign, Award,
  Clock, ImageOff,
  MessageCircle, Store, AlertTriangle,
  ChevronLeft, ChevronRight, TrendingUp, Coins, Sparkles, Save, Loader2, Info,
  Check,
  ClipboardList,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { CarregandoFormulario, CarregandoGrade } from "../components/carregando";
import { Switch } from "../components/ui/switch";
import { CampoDesconto, ListaFlutuante, erroDaPizza } from "@/features/promocao-form";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const API = "/api";
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `/api${url.startsWith("/") ? url : "/" + url}`;
};

const WEEKDAYS = [
  { key: "seg", label: "Seg" },
  { key: "ter", label: "Ter" },
  { key: "qua", label: "Qua" },
  { key: "qui", label: "Qui" },
  { key: "sex", label: "Sex" },
  { key: "sab", label: "Sáb" },
  { key: "dom", label: "Dom" },
];

const MODULES = [
  { key: "Cardapio", label: "Cardápio", icon: Store },
  { key: "Delivery", label: "Delivery", icon: MotoEntregaIcon },
  { key: "Mesas", label: "Mesas", icon: MesaIcon },
  { key: "Comandas", label: "Comandas", icon: ClipboardList },
  { key: "Balcao", label: "Balcão", icon: CaixaRegistradoraIcon },
  // A chave gravada continua "ChatBot"; é o pedido anotado pela CLEO no WhatsApp.
  { key: "ChatBot", label: "WhatsApp", icon: MessageCircle },
];

const DISCOUNT_TYPES = [
  { key: "percentage", label: "% de Desconto", icon: Percent, color: "text-blue-500" },
  { key: "fixed_price", label: "Valor Fixo (R$)", icon: DollarSign, color: "text-green-500" },
  // Pontuação não existe no sistema: saiu da criação (pedido do dono, 23/10/2026)
  // e só fica na lista pra exibir promoção antiga gravada com discount_type='points'.
  { key: "points", label: "Pontuação", icon: Award, color: "text-purple-500", disabled: true },
];

/** Detecta se um produto é pizza pelo `order_steps`. Critério idêntico ao
 * do backend (`database.py` — `s.get("kind") == "pizza_size"`). */
function isPizza(product) {
  return Array.isArray(product?.order_steps)
    && product.order_steps.some((s) => s && s.kind === "pizza_size");
}

/** Tamanhos de uma pizza vivem dentro do próprio produto, no step
 * `kind == "pizza_size"`. O endpoint global `/api/pizza/sizes` foi removido
 * em v1.5.208 — cada pizza tem o seu próprio conjunto. Retorna sempre array. */
function getPizzaSizesOf(product) {
  if (!product?.order_steps) return [];
  const step = product.order_steps.find((s) => s && s.kind === "pizza_size");
  return Array.isArray(step?.items) ? step.items : [];
}

/** Calcula o preço final após o desconto. Espelha exatamente a lógica do
 * backend em `pedidos/validation.py::expected_price`:
 *  - percentage: `base * (1 - dvalue/100)`
 *  - fixed_price: `dvalue` (preço final, NÃO desconto absoluto)
 * Retorna null se não dá pra calcular (valor inválido ou tipo desconhecido). */
function calcFinalPrice(basePrice, discountType, discountValue) {
  const base = Number(basePrice);
  const dval = Number(discountValue);
  if (!Number.isFinite(base) || base <= 0) return null;
  if (!Number.isFinite(dval) || dval <= 0) return null;
  if (discountType === "percentage") {
    if (dval > 100) return null;
    return Math.round(base * (1 - dval / 100) * 100) / 100;
  }
  if (discountType === "fixed_price") {
    if (dval >= base) return null; // preço final acima do base — não faz sentido
    return Math.round(dval * 100) / 100;
  }
  return null;
}

/** Formata um número como R$ pt-BR. Usado nos badges de preço. */
function fmtBRL(n) {
  if (!Number.isFinite(Number(n))) return "—";
  return `R$ ${Number(n).toFixed(2).replace(".", ",")}`;
}

const emptyForm = {
  name: "",
  description: "",
  products: [], // [{product_id, discount_value}]
  discount_type: "percentage",
  valid_weekdays: [],
  time_from: "",
  time_until: "",
  // Todos os módulos marcados (Comandas entrou em 24/10/2026 e ficava de fora).
  modules: MODULES.map((m) => m.key),
  is_active: true,
  accepts_coupon: false,
  usage_limit: "",  // string vazia = ilimitada; positivo = limite global de unidades vendidas
  segmentos: [],  // para quem vale: tipos de cliente (27/10/2026); vazio = todos
};

const WIZARD_STEPS = [
  { key: "what", label: "O que", desc: "Nome + Desconto + Produtos" },
  { key: "when", label: "Quando", desc: "Dias, horário, módulos e clientes" },
  { key: "limits", label: "Limites", desc: "Quantidade e regras" },
];

export default function Promocoes() {
  const [promotions, setPromotions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  // Carregamento com cards só na primeira abertura (promoções + produtos).
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [productSearch, setProductSearch] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  // Para quem vale (27/10/2026): filtro da lista e o tipo que chega do botão de ofertas da tela de Clientes
  const [filtroTipo, setFiltroTipo] = useState("");
  const [params, setParams] = useSearchParams();
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  // Promoção pendente de exclusão (substitui window.confirm — que renderiza o
  // chrome do navegador "uaipizza.usenucleo.com.br diz" no Electron e fica feio).
  const [pendingDelete, setPendingDelete] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const [wizardStep, setWizardStep] = useState(0); // 0=O que, 1=Quando, 2=Limites
  // Cache de preços por produto: {productId: {sizeId: priceNumber}}
  // Carregado sob demanda quando uma pizza entra no formData; usado pra mostrar
  // "R$ X → R$ Y" inline no input de cada tamanho.
  const [pizzaPricesByProductId, setPizzaPricesByProductId] = useState({});
  const dropdownRef = useRef(null);
  const listaRef = useRef(null);

  const fetchPromotions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/promotions`, getAuthHeader());
      setPromotions(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Erro ao carregar promoções");
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products`, getAuthHeader());
      const prods = (Array.isArray(res.data) ? res.data : []).filter(p => !p.is_insumo);
      setAllProducts(prods);
    } catch {}
  }, []);

  useEffect(() => {
    // Espera os produtos também: sem eles o card mostraria "?" no nome do item.
    Promise.allSettled([fetchPromotions(), fetchProducts()]).finally(() => setPrimeiraCarga(false));
  }, [fetchPromotions, fetchProducts]);

  // Body scroll lock + Esc fecha — só enquanto o modal está aberto.
  useEffect(() => {
    if (!showModal) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") tryCloseModal(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, formDirty]);

  /** Carrega os preços de uma pizza específica e guarda no cache.
   * Endpoint: GET /pizza/products/{id}/prices → {prices: {sizeId: number}}
   * (ou {sizeId: number} direto — aceita os dois formatos pra ser tolerante). */
  const fetchPizzaPricesFor = useCallback(async (productId) => {
    if (!productId) return;
    try {
      const res = await axios.get(`${API}/pizza/products/${productId}/prices`, getAuthHeader());
      const data = res.data?.prices || res.data || {};
      const normalized = Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, Number(v) || 0]),
      );
      setPizzaPricesByProductId((cur) => ({ ...cur, [productId]: normalized }));
    } catch {
      setPizzaPricesByProductId((cur) => ({ ...cur, [productId]: {} }));
    }
  }, []);

  // Close product dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      // A lista flutua fora do campo (portal no <body>): clique nela não fecha
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)
        && !(listaRef.current && listaRef.current.contains(e.target))) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const resetForm = () => {
    setFormData({ ...emptyForm, products: [] });
    setEditing(null);
    setProductSearch("");
    setFormDirty(false);
    setWizardStep(0);
  };

  const openNew = () => {
    resetForm();
    setShowModal(true);
  };

  // ?para=recorrente: abre a Nova promoção já para aquele tipo de cliente
  useEffect(() => {
    const para = tipoDaUrl(params);
    if (!para) return;
    setEditing(null);
    setFormData({ ...emptyForm, products: [], segmentos: [para] });
    setProductSearch("");
    setFormDirty(false);
    setWizardStep(0);
    setShowModal(true);
    setParams((p) => { const n = new URLSearchParams(p); n.delete("para"); return n; }, { replace: true });
  }, [params, setParams]);

  const openEdit = (promo) => {
    setEditing(promo);
    const promoProducts = promo.products || [];
    setFormData({
      name: promo.name || "",
      description: promo.description || "",
      products: promoProducts.map(p => ({
        product_id: p.product_id,
        discount_value: p.discount_value || "",
        ...(p.discount_value_combo !== undefined ? { discount_value_combo: p.discount_value_combo || "" } : {}),
        // Hidrata o map de tamanhos da pizza: backend pode devolver `sizes`
        // como dict ou null/undefined. Normaliza pra string (input controlado).
        ...(p.sizes && typeof p.sizes === "object" ? {
          sizes: Object.fromEntries(
            Object.entries(p.sizes).map(([k, v]) => [k, v == null ? "" : String(v)]),
          ),
        } : {}),
      })),
      discount_type: promo.discount_type || "percentage",
      valid_weekdays: promo.valid_weekdays || [],
      time_from: promo.time_from || "",
      time_until: promo.time_until || "",
      // Sem lista gravada = vale em todos (inclusive Comandas); salvar não pode tirar nenhum.
      modules: promo.modules || MODULES.map((m) => m.key),
      is_active: promo.is_active !== false,
      accepts_coupon: promo.accepts_coupon === 1 || promo.accepts_coupon === true,
      usage_limit: promo.usage_limit ? String(promo.usage_limit) : "",
      segmentos: promo.segmentos || [],
    });
    setProductSearch("");
    setFormDirty(false);
    setWizardStep(0);
    setShowModal(true);
    // Pré-carrega preços de TODAS as pizzas que já estão no formData (vindas
    // do promo carregado). Sem isso, o "R$X → R$Y" não aparece até o usuário
    // mexer no input.
    promoProducts.forEach((pp) => {
      const prod = allProducts.find((x) => x.id === pp.product_id);
      if (isPizza(prod) && !pizzaPricesByProductId[pp.product_id]) {
        fetchPizzaPricesFor(pp.product_id);
      }
    });
  };

  const tryCloseModal = () => {
    if (formDirty) {
      setShowDiscardConfirm(true);
    } else {
      setShowModal(false);
      resetForm();
    }
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    setShowModal(false);
    resetForm();
  };

  const markDirty = () => { if (!formDirty) setFormDirty(true); };

  const addProduct = (product) => {
    if (formData.products.some(p => p.product_id === product.id)) {
      toast.error("Produto já adicionado");
      return;
    }
    const isCombo = product.product_type === "combo";
    const pizza = isPizza(product);
    // Pizza: 1 desconto por tamanho. Tamanhos vêm do PRÓPRIO produto via
    // `order_steps[kind=pizza_size].items`. Cada pizza pode ter seu conjunto
    // de tamanhos (endpoint global /api/pizza/sizes foi removido na v1.5.208).
    const sizesOfThisPizza = pizza ? getPizzaSizesOf(product) : [];
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, {
        product_id: product.id,
        discount_value: "",
        ...(isCombo ? { discount_value_combo: "" } : {}),
        ...(pizza ? { sizes: sizesOfThisPizza.reduce((acc, s) => ({ ...acc, [s.id]: "" }), {}) } : {}),
      }]
    }));
    setProductSearch("");
    setShowProductDropdown(false);
    markDirty();
    // Pra pizza, pré-carrega a matriz de preços pra mostrar "R$ X → R$ Y" inline.
    if (pizza && !pizzaPricesByProductId[product.id]) {
      fetchPizzaPricesFor(product.id);
    }
  };

  /** Atualiza desconto de UM tamanho específico da pizza dentro de products[].sizes. */
  const updateProductSizeDiscount = (productId, sizeId, value) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map(p =>
        p.product_id === productId
          ? { ...p, sizes: { ...(p.sizes || {}), [sizeId]: value } }
          : p,
      ),
    }));
    markDirty();
  };

  const removeProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter(p => p.product_id !== productId)
    }));
    markDirty();
  };

  const updateProductDiscount = (productId, field, value) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map(p =>
        p.product_id === productId ? { ...p, [field]: value } : p
      )
    }));
    markDirty();
  };

  const getProduct = (id) => allProducts.find(p => p.id === id);

  /** Valida o step atual antes de avançar. Retorna mensagem de erro ou null. */
  const validateStep = (stepIdx) => {
    if (stepIdx === 0) {
      if (!formData.name.trim()) return "Dê um nome para a promoção";
      if (formData.products.length === 0) return "Adicione pelo menos um produto";
      for (const p of formData.products) {
        const prod = getProduct(p.product_id);
        const pizza = isPizza(prod);
        const isCombo = prod?.product_type === "combo";
        if (pizza) {
          // Cada tamanho preenchido passa pelas mesmas regras do produto
          // (01/11/2026: antes a pizza pulava e aceitava "por" acima do preço e 100%).
          const erroPizza = erroDaPizza({
            tipo: formData.discount_type, sizes: p.sizes, precos: pizzaPricesByProductId[p.product_id],
            tamanhos: getPizzaSizesOf(prod), nome: prod?.name || "produto",
          });
          if (erroPizza) return erroPizza;
          continue;
        }
        const val = parseFloat(p.discount_value);
        if (isNaN(val) || val <= 0) return `Defina o desconto para ${prod?.name || "produto"}`;
        if (isCombo) {
          const valCombo = parseFloat(p.discount_value_combo);
          if (isNaN(valCombo) || valCombo <= 0) return `Defina o desconto combo para ${prod?.name || "produto"}`;
        }
        if (formData.discount_type === "percentage" && (val >= 100 || (isCombo && parseFloat(p.discount_value_combo) >= 100))) {
          return `O desconto de ${prod?.name || "produto"} precisa ser menor que 100%`;
        }
        // Valor fixo = preço final: igual ou acima do preço normal não é promoção (27/10/2026)
        if (formData.discount_type === "fixed_price") {
          const nome = prod?.name || "produto";
          const base = isCombo ? (prod?.simple_price || Math.round((prod?.sale_price || 0) * 0.7 * 100) / 100) : (prod?.sale_price || 0);
          if (base > 0 && val >= base) return `O preço de ${nome} na promoção precisa ser menor que ${fmtBRL(base)}`;
          if (isCombo && prod?.sale_price > 0 && parseFloat(p.discount_value_combo) >= prod.sale_price) {
            return `O preço do combo de ${nome} na promoção precisa ser menor que ${fmtBRL(prod.sale_price)}`;
          }
        }
      }
    }
    if (stepIdx === 1) {
      if (formData.modules.length === 0) return "Selecione ao menos um módulo";
    }
    if (stepIdx === 2) {
      if (formData.usage_limit && parseInt(formData.usage_limit, 10) < 1) {
        return "Limite de unidades precisa ser maior que zero";
      }
    }
    return null;
  };

  const goToStep = (target) => {
    if (target === wizardStep) return;
    if (target < wizardStep) { setWizardStep(target); return; }
    // Avanço: valida steps anteriores
    for (let i = wizardStep; i < target; i++) {
      const err = validateStep(i);
      if (err) return toast.error(err);
    }
    setWizardStep(target);
  };

  const goNext = () => {
    const err = validateStep(wizardStep);
    if (err) return toast.error(err);
    if (wizardStep < WIZARD_STEPS.length - 1) setWizardStep(wizardStep + 1);
  };

  const goBack = () => {
    if (wizardStep > 0) setWizardStep(wizardStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guarda: se o form foi submetido por Enter num input ou botão mal-marcado
    // enquanto o user ainda está num step intermediário, avança o wizard em vez
    // de salvar. Salvar SÓ acontece pelo botão explícito do último step.
    if (wizardStep < WIZARD_STEPS.length - 1) {
      goNext();
      return;
    }
    if (!formData.name.trim()) return toast.error("Nome é obrigatório");
    if (formData.products.length === 0) return toast.error("Adicione pelo menos um produto");

    // Valida cada produto. Pizza precisa de AO MENOS 1 tamanho com valor > 0
    // (não exige todos — lojista pode promover só Grande/Família, p. ex.).
    // Não-pizza usa discount_value normal.
    for (const p of formData.products) {
      const prod = getProduct(p.product_id);
      const isCombo = prod?.product_type === "combo";
      const pizza = isPizza(prod);
      if (pizza) {
        const erroPizza = erroDaPizza({
          tipo: formData.discount_type, sizes: p.sizes, precos: pizzaPricesByProductId[p.product_id],
          tamanhos: getPizzaSizesOf(prod), nome: prod?.name || "produto",
        });
        if (erroPizza) return toast.error(erroPizza);
        continue;
      }
      const val = parseFloat(p.discount_value);
      if (isNaN(val) || val <= 0) {
        return toast.error(`Defina o valor ${isCombo ? "simples" : ""} para ${prod?.name || "produto"}`);
      }
      if (isCombo) {
        const valCombo = parseFloat(p.discount_value_combo);
        if (isNaN(valCombo) || valCombo <= 0) {
          return toast.error(`Defina o valor combo para ${prod?.name || "produto"}`);
        }
      }
    }

    const payload = {
      name: formData.name,
      description: formData.description,
      discount_type: formData.discount_type,
      products: formData.products.map(p => {
        const prod = getProduct(p.product_id);
        const pizza = isPizza(prod);
        if (pizza) {
          // Pizza: só envia tamanhos com valor numérico > 0. Demais tamanhos
          // ficam fora da promoção (não viram entrada com 0).
          const cleanSizes = Object.fromEntries(
            Object.entries(p.sizes || {})
              .map(([k, v]) => [k, parseFloat(v)])
              .filter(([, v]) => !isNaN(v) && v > 0),
          );
          return { product_id: p.product_id, sizes: cleanSizes };
        }
        const entry = { product_id: p.product_id, discount_value: parseFloat(p.discount_value) };
        if (prod?.product_type === "combo") {
          entry.discount_value_combo = parseFloat(p.discount_value_combo);
        }
        return entry;
      }),
      valid_weekdays: formData.valid_weekdays.length > 0 ? formData.valid_weekdays : null,
      time_from: formData.time_from || null,
      time_until: formData.time_until || null,
      modules: formData.modules.length > 0 ? formData.modules : null,
      is_active: formData.is_active,
      accepts_coupon: formData.accepts_coupon,
      segmentos: formData.segmentos || [],
      // usage_limit: "" → null (ilimitada); inteiro positivo = limite global.
      usage_limit: formData.usage_limit && parseInt(formData.usage_limit, 10) > 0
        ? parseInt(formData.usage_limit, 10)
        : null,
    };

    try {
      if (editing) {
        await axios.put(`${API}/promotions/${editing.id}`, payload, getAuthHeader());
        toast.success("Promoção atualizada!");
      } else {
        await axios.post(`${API}/promotions`, payload, getAuthHeader());
        toast.success("Promoção criada!");
      }
      setShowModal(false);
      resetForm();
      fetchPromotions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar promoção");
    }
  };

  const handleDelete = async (id) => {
    // Abre o AlertDialog customizado em vez de window.confirm — confirm nativo
    // renderiza o chrome do navegador ("uaipizza.usenucleo.com.br diz") que
    // não combina com o resto da UI do app.
    setPendingDelete(promotions.find(p => p.id === id) || { id });
  };

  const confirmDelete = async () => {
    const id = pendingDelete?.id;
    setPendingDelete(null);
    if (!id) return;
    try {
      await axios.delete(`${API}/promotions/${id}`, getAuthHeader());
      toast.success("Promoção excluída!");
      fetchPromotions();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(`${API}/promotions/${promo.id}`, { is_active: !promo.is_active }, getAuthHeader());
      toast.success(promo.is_active ? "Promoção desativada" : "Promoção ativada");
      fetchPromotions();
    } catch {
      toast.error("Erro ao alterar status");
    }
  };

  const filteredDropdownProducts = allProducts.filter(p =>
    (p.name || "").toLowerCase().includes(productSearch.toLowerCase()) &&
    !formData.products.some(fp => fp.product_id === p.id)
  );

  const filteredPromotions = promotions.filter(p => {
    if (!ofertaPassa(p.segmentos, filtroTipo)) return false;
    if (!searchFilter) return true;
    const term = searchFilter.toLowerCase();
    if ((p.name || "").toLowerCase().includes(term)) return true;
    // Search in products
    const prods = p.products || [];
    return prods.some(pp => {
      const prod = getProduct(pp.product_id);
      return (prod?.name || "").toLowerCase().includes(term);
    });
  });

  const discountLabel = (type, value) => {
    if (type === "percentage") return `${value}%`;
    if (type === "fixed_price") return `R$ ${parseFloat(value).toFixed(2).replace(".", ",")}`;
    return `${value} pts`;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
              <Gift className="w-7 h-7 text-green-500" />
              Promoções
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">Gerencie suas promoções e ofertas especiais</p>
          </div>
          <Button onClick={openNew} className="gap-2 bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4" /> Nova Promoção
          </Button>
        </div>

        {/* Search + para quem vale */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar promoção ou produto..."
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50"
            />
          </div>
          <FiltroTipoCliente valor={filtroTipo} aoMudar={setFiltroTipo} e2e="promocoes-filtro-tipo" />
        </div>

        {/* List */}
        {primeiraCarga ? (
          <CarregandoGrade quantos={6} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" altura="h-[190px]" />
        ) : filteredPromotions.length === 0 ? (
          <div className="text-center py-16 bg-card rounded-xl border">
            <Gift className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">Nenhuma promoção cadastrada</p>
            <Button onClick={openNew} variant="outline" className="mt-3 gap-2">
              <Plus className="w-4 h-4" /> Criar primeira promoção
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredPromotions.map(promo => {
              const promoProducts = promo.products || [];
              const dtInfo = DISCOUNT_TYPES.find(d => d.key === promo.discount_type) || DISCOUNT_TYPES[0];
              const DtIcon = dtInfo.icon;
              return (
                <div key={promo.id} className={`bg-card rounded-xl border overflow-hidden transition-all hover:shadow-md ${!promo.is_active ? "opacity-60" : ""}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <h3 className="font-semibold text-sm">{promo.name}</h3>
                        {promo.description && <p className="text-xs text-muted-foreground mt-0.5">{promo.description}</p>}
                      </div>
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${
                        promo.discount_type === "percentage" ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" :
                        promo.discount_type === "fixed_price" ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400" :
                        "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                      }`}>
                        <DtIcon className="w-3 h-3" />
                        {dtInfo.label}
                      </span>
                    </div>

                    {/* Products grid */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {promoProducts.map((pp, idx) => {
                        const prod = getProduct(pp.product_id);
                        const photoSrc = prod ? getImageUrl(prod.photo_url) : null;
                        const isCombo = prod?.product_type === "combo";
                        const pizzaItem = isPizza(prod) && pp.sizes && typeof pp.sizes === "object";
                        const sizeEntries = pizzaItem ? Object.entries(pp.sizes).filter(([, v]) => v != null && v !== "") : [];
                        return (
                          <div key={idx} className="flex items-center gap-2 bg-muted/50 rounded-lg px-2 py-1.5">
                            {photoSrc ? (
                              <img src={photoSrc} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" onError={e => { e.target.style.display = "none"; }} />
                            ) : (
                              <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                                <ImageOff className="w-3 h-3 text-muted-foreground/40" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-xs font-medium truncate max-w-[140px]">{prod?.name || "?"}</p>
                              {pizzaItem ? (
                                <p className="text-[10px] text-green-600 dark:text-green-400">
                                  {sizeEntries.length} tamanho{sizeEntries.length === 1 ? "" : "s"} em promo
                                </p>
                              ) : isCombo && pp.discount_value_combo ? (
                                <div className="flex gap-1.5">
                                  <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400">S: {discountLabel(promo.discount_type, pp.discount_value)}</span>
                                  <span className="text-[10px] font-bold text-green-600 dark:text-green-400">C: {discountLabel(promo.discount_type, pp.discount_value_combo)}</span>
                                </div>
                              ) : (
                                <p className="text-[10px] font-bold text-green-600 dark:text-green-400">
                                  {discountLabel(promo.discount_type, pp.discount_value)}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Details */}
                    <div className="flex flex-wrap gap-1.5">
                      {promo.valid_weekdays && promo.valid_weekdays.length > 0 && promo.valid_weekdays.length < 7 && (
                        <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-medium">
                          {promo.valid_weekdays.map(d => WEEKDAYS.find(w => w.key === d)?.label || d).join(", ")}
                        </span>
                      )}
                      {promo.time_from && promo.time_until && (
                        <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                          <Clock className="w-2.5 h-2.5" /> {promo.time_from} - {promo.time_until}
                        </span>
                      )}
                    </div>

                    {/* Modules */}
                    {promo.modules && promo.modules.length > 0 && promo.modules.length < MODULES.length && (
                      <div className="flex gap-1 mt-1.5">
                        {promo.modules.map(m => {
                          const mod = MODULES.find(x => x.key === m);
                          const MIcon = mod?.icon || Store;
                          return (
                            <span key={m} className="text-[9px] bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                              <MIcon className="w-2.5 h-2.5" /> {mod?.label || m}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Para quem vale (27/10/2026) */}
                    {(promo.segmentos || []).length > 0 && (
                      <div className="mt-1.5" data-e2e="promo-so-para">
                        <span className="text-[10px] bg-violet-500/10 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded font-semibold">
                          Só para: {nomesTipos(promo.segmentos)}
                        </span>
                      </div>
                    )}

                    {/* Barra de progresso quando há limite de unidades */}
                    {promo.usage_limit ? (() => {
                      const used = promo.usage_count || 0;
                      const lim = promo.usage_limit;
                      const pct = Math.min(100, Math.round((used / lim) * 100));
                      const exhausted = pct >= 100;
                      return (
                        <div className="mb-2">
                          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                            <span className="inline-flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              {exhausted ? "Esgotada" : "Vendidas"}
                            </span>
                            <span className="font-bold tabular-nums">
                              {used} / {lim}
                            </span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className={[
                                "h-full rounded-full transition-all",
                                exhausted ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-green-500",
                              ].join(" ")}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })() : null}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between px-4 py-2 border-t bg-muted/20">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      <Switch checked={!!promo.is_active} onCheckedChange={() => toggleActive(promo)} />
                      {promo.is_active ? "Ativa" : "Inativa"}
                    </label>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(promo)} className="h-7 w-7 p-0">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(promo.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4" onClick={tryCloseModal}>
          <div className="bg-card rounded-2xl border border-green-500/20 shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-green-500/10 to-emerald-500/5 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shrink-0">
                  <Gift className="w-5 h-5 text-white" strokeWidth={2.2} />
                </div>
                <div>
                  <h2 className="text-lg font-bold leading-tight">
                    {editing ? "Editar Promoção" : "Nova Promoção"}
                  </h2>
                  <p className="text-[11px] text-muted-foreground">
                    Configure desconto, produtos e regras de aplicação
                  </p>
                </div>
              </div>
              <button onClick={tryCloseModal} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground" title="Fechar (Esc)">
                <X className="w-5 h-5" />
              </button>
            </div>

            <WizardSteps current={wizardStep} onGo={goToStep} />

            <form id="promo-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto overscroll-contain p-5 space-y-5">
              {wizardStep === 0 && (<>
              {/* Nome */}
              <div>
                <label className="text-sm font-medium mb-1 block">Nome da Promoção *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => { setFormData(p => ({ ...p, name: e.target.value })); markDirty(); }}
                  placeholder="Ex: Combo Sexta-feira"
                  className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50"
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="text-sm font-medium mb-1 block">Descrição</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={e => { setFormData(p => ({ ...p, description: e.target.value })); markDirty(); }}
                  placeholder="Descrição opcional"
                  className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50"
                />
              </div>

              {/* Tipo de Desconto */}
              <div>
                <label className="text-sm font-medium mb-2 block">Tipo de Desconto *</label>
                <div className="grid grid-cols-2 gap-2">
                  {DISCOUNT_TYPES.filter(dt => !dt.disabled).map(dt => {
                    const Icon = dt.icon;
                    const selected = formData.discount_type === dt.key;
                    const disabled = !!dt.disabled;
                    return (
                      <button
                        key={dt.key}
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                          if (disabled) return;
                          setFormData(p => ({ ...p, discount_type: dt.key }));
                          markDirty();
                        }}
                        title={disabled ? "Em breve — feature planejada" : undefined}
                        className={`relative flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all text-xs font-medium ${
                          disabled
                            ? "border-transparent bg-muted/30 text-muted-foreground/50 cursor-not-allowed"
                            : selected
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                            : "border-transparent bg-muted/50 hover:bg-muted text-muted-foreground"
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${selected && !disabled ? dt.color : ""}`} />
                        {dt.label}
                        {disabled && (
                          <span className="absolute top-1 right-1 text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400">
                            Em breve
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Produtos */}
              <div>
                <label className="text-sm font-medium mb-2 block">Produtos *</label>

                {/* Added products */}
                {formData.products.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {formData.products.map((fp) => {
                      const prod = getProduct(fp.product_id);
                      const photoSrc = prod ? getImageUrl(prod.photo_url) : null;
                      const isCombo = prod?.product_type === "combo";
                      const pizza = isPizza(prod);
                      const sizesOfThisPizza = pizza ? getPizzaSizesOf(prod) : [];
                      const discountPrefix = formData.discount_type === "percentage" ? "%" :
                        formData.discount_type === "fixed_price" ? "R$" : "pts";
                      return (
                        <div key={fp.product_id} className="bg-muted/30 rounded-lg p-2 border">
                          <div className="flex items-center gap-2">
                            {photoSrc ? (
                              <img src={photoSrc} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" onError={e => { e.target.style.display = "none"; }} />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                <ImageOff className="w-4 h-4 text-muted-foreground/40" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate flex items-center gap-1.5">
                                {prod?.name || "Produto removido"}
                                {pizza && (
                                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-700 dark:text-orange-300">
                                    Pizza
                                  </span>
                                )}
                              </p>
                              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                {pizza ? (
                                  <span>{sizesOfThisPizza.length} tamanho{sizesOfThisPizza.length === 1 ? "" : "s"} disponíveis</span>
                                ) : isCombo ? (
                                  <>
                                    <span>Simples: R$ {(prod?.simple_price || Math.round((prod?.sale_price || 0) * 0.7 * 100) / 100).toFixed(2).replace(".", ",")}</span>
                                    <span>Combo: R$ {(prod?.sale_price || 0).toFixed(2).replace(".", ",")}</span>
                                  </>
                                ) : (
                                  <span>R$ {(prod?.sale_price || 0).toFixed(2).replace(".", ",")}</span>
                                )}
                              </div>
                            </div>
                            <button type="button" onClick={() => removeProduct(fp.product_id)} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 flex-shrink-0">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          {/* Discount inputs */}
                          {pizza ? (
                            <div className="mt-2 space-y-1">
                              <p className="text-[10px] text-muted-foreground">
                                Deixe em branco pra não promover aquele tamanho.
                              </p>
                              {sizesOfThisPizza.length === 0 ? (
                                <p className="text-[11px] text-amber-600 dark:text-amber-400 italic">
                                  Esta pizza não tem tamanhos definidos no step "Tamanho" do produto. Edite o produto em Cardápio → Produtos.
                                </p>
                              ) : (
                                sizesOfThisPizza.map((sz) => {
                                  const sizeDiscount = (fp.sizes && fp.sizes[sz.id]) || "";
                                  const base = pizzaPricesByProductId[fp.product_id]?.[sz.id];
                                  const final = calcFinalPrice(base, formData.discount_type, sizeDiscount);
                                  const hasInput = sizeDiscount !== "" && sizeDiscount !== null;
                                  return (
                                    <PizzaSizeRow
                                      key={sz.id}
                                      sizeName={sz.name}
                                      basePrice={base}
                                      finalPrice={final}
                                      hasInput={hasInput}
                                      discountPrefix={discountPrefix}
                                      discountType={formData.discount_type}
                                      value={sizeDiscount}
                                      onChange={(v) => updateProductSizeDiscount(fp.product_id, sz.id, sanitizeCurrencyInput(v))}
                                    />
                                  );
                                })
                              )}
                            </div>
                          ) : isCombo ? (
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <ComboDiscountRow
                                label="Simples"
                                color="text-blue-600 dark:text-blue-400"
                                discountPrefix={discountPrefix}
                                value={fp.discount_value}
                                onChange={(v) => updateProductDiscount(fp.product_id, "discount_value", sanitizeCurrencyInput(v))}
                                basePrice={prod?.simple_price || Math.round((prod?.sale_price || 0) * 0.7 * 100) / 100}
                                discountType={formData.discount_type}
                              />
                              <ComboDiscountRow
                                label="Combo"
                                color="text-green-600 dark:text-green-400"
                                discountPrefix={discountPrefix}
                                value={fp.discount_value_combo || ""}
                                onChange={(v) => updateProductDiscount(fp.product_id, "discount_value_combo", sanitizeCurrencyInput(v))}
                                basePrice={prod?.sale_price || 0}
                                discountType={formData.discount_type}
                              />
                            </div>
                          ) : (
                            <SimpleDiscountRow
                              discountPrefix={discountPrefix}
                              value={fp.discount_value}
                              onChange={(v) => updateProductDiscount(fp.product_id, "discount_value", sanitizeCurrencyInput(v))}
                              basePrice={prod?.sale_price || 0}
                              discountType={formData.discount_type}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Product search */}
                <div className="relative" ref={dropdownRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={e => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                      onFocus={() => setShowProductDropdown(true)}
                      placeholder="Buscar e adicionar produto..."
                      className="w-full pl-9 pr-4 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50"
                    />
                  </div>
                  {showProductDropdown && productSearch && (
                    <ListaFlutuante ancoraRef={dropdownRef} listaRef={listaRef}>
                      {filteredDropdownProducts.length === 0 ? (
                        <p className="p-3 text-sm text-muted-foreground">Nenhum produto encontrado</p>
                      ) : (
                        filteredDropdownProducts.slice(0, 20).map(p => {
                          const src = getImageUrl(p.photo_url);
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => addProduct(p)}
                              className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted transition-colors text-sm"
                            >
                              {src ? (
                                <img src={src} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" onError={e => { e.target.style.display = "none"; }} />
                              ) : (
                                <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                                  <ImageOff className="w-3 h-3 text-muted-foreground/40" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{p.name}</p>
                                <p className="text-xs text-muted-foreground">R$ {(p.sale_price || 0).toFixed(2).replace(".", ",")}</p>
                              </div>
                              <Plus className="w-4 h-4 text-green-500 flex-shrink-0" />
                            </button>
                          );
                        })
                      )}
                    </ListaFlutuante>
                  )}
                </div>
              </div>
              </>)}

              {wizardStep === 1 && (<>
              {/* Dias da semana */}
              <div>
                <label className="text-sm font-medium mb-2 block">Dias Disponíveis</label>
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map(d => {
                    const sel = formData.valid_weekdays.includes(d.key);
                    return (
                      <button
                        key={d.key}
                        type="button"
                        onClick={() => { setFormData(p => ({
                          ...p,
                          valid_weekdays: sel
                            ? p.valid_weekdays.filter(x => x !== d.key)
                            : [...p.valid_weekdays, d.key]
                        })); markDirty(); }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          sel ? "bg-green-500 text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
                        }`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Deixe vazio para todos os dias</p>
              </div>

              {/* Horário */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Horário Início</label>
                  <input type="time" value={formData.time_from} onChange={e => { setFormData(p => ({ ...p, time_from: e.target.value })); markDirty(); }}
                    className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Horário Fim</label>
                  <input type="time" value={formData.time_until} onChange={e => { setFormData(p => ({ ...p, time_until: e.target.value })); markDirty(); }}
                    className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50" />
                </div>
              </div>

              {/* Módulos */}
              <div>
                <label className="text-sm font-medium mb-2 block">Módulos Disponíveis</label>
                <div className="flex flex-wrap gap-2">
                  {MODULES.map(m => {
                    const sel = formData.modules.includes(m.key);
                    const MIcon = m.icon;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => { setFormData(p => ({
                          ...p,
                          modules: sel
                            ? p.modules.filter(x => x !== m.key)
                            : [...p.modules, m.key]
                        })); markDirty(); }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          sel ? "bg-orange-500 text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
                        }`}
                      >
                        <MIcon className="w-3.5 h-3.5" />
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Para quem vale: tipos de cliente da tela de Clientes (27/10/2026) */}
              <div className="pt-4 border-t">
                <ParaQuemVale valor={formData.segmentos} e2e="promo-para-quem"
                  aoMudar={(segmentos) => { setFormData(p => ({ ...p, segmentos })); markDirty(); }}
                  ajuda="Marcado, o preço de promoção só aparece e só vale para o cliente identificado desses tipos: no cardápio, quem entrou com o telefone; no Delivery, quando o cliente é escolhido. O visitante sem login vê o preço normal com o aviso de preço especial." />
                {(formData.segmentos || []).length > 0 && formData.products.some(p => p.sizes) && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                    Pizza por tamanho ainda não tem preço exclusivo por tipo de cliente: nessa promoção, os tamanhos de pizza não entram.
                  </p>
                )}
              </div>
              </>)}

              {wizardStep === 2 && (<>
              {/* Limite de quantidade */}
              <UsageLimitField
                editing={editing}
                value={formData.usage_limit}
                onChange={(v) => { setFormData(p => ({ ...p, usage_limit: v })); markDirty(); }}
              />

              {/* Ativo: o Switch padrão do sistema (dono, 27/10/2026), no lugar do ícone de chave */}
              <div className="flex items-center justify-between py-2 border-t pt-4">
                <Label htmlFor="promo-ativa" className="text-sm font-medium cursor-pointer">Promoção Ativa</Label>
                <Switch
                  id="promo-ativa"
                  data-e2e="promo-ativa"
                  checked={!!formData.is_active}
                  onCheckedChange={(v) => { setFormData(p => ({ ...p, is_active: v })); markDirty(); }}
                />
              </div>

              {/* Aceita Cupom */}
              <div className="flex items-center justify-between py-2 border-t pt-3">
                <div className="pr-3">
                  <Label htmlFor="promo-aceita-cupom" className="text-sm font-medium cursor-pointer">Aceita Cupom</Label>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Se desligado, nenhum cupom será aplicado aos produtos desta promoção e eles nem contam no valor mínimo do cupom.
                  </p>
                </div>
                <Switch
                  id="promo-aceita-cupom"
                  data-e2e="promo-aceita-cupom"
                  className="shrink-0"
                  checked={!!formData.accepts_coupon}
                  onCheckedChange={(v) => { setFormData(p => ({ ...p, accepts_coupon: v })); markDirty(); }}
                />
              </div>
              </>)}

            </form>
            {/* Footer sticky com navegação do wizard. */}
            <div className="border-t bg-muted/30 px-5 py-3.5 flex gap-2 shrink-0">
              {/* `key` diferente em cada botão: sem ele o React reaproveita o mesmo
                  <button> e, ao clicar em "Próximo" no passo 2, o botão já vira
                  type=submit durante o clique e o navegador envia o formulário —
                  a promoção era criada e o card fechava sozinho (bug de 23/10/2026). */}
              {wizardStep > 0 ? (
                <Button key="voltar" type="button" variant="outline" className="flex-1 gap-1" onClick={goBack}>
                  <ChevronLeft className="w-4 h-4" /> Voltar
                </Button>
              ) : (
                <Button key="cancelar" type="button" variant="outline" className="flex-1" onClick={tryCloseModal}>
                  Cancelar
                </Button>
              )}
              {wizardStep < WIZARD_STEPS.length - 1 ? (
                <Button key="proximo" type="button" className="flex-1 bg-green-600 hover:bg-green-700 gap-1" onClick={goNext}>
                  Próximo: {WIZARD_STEPS[wizardStep + 1].label} <ChevronRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button key="salvar" type="submit" form="promo-form" className="flex-1 bg-green-600 hover:bg-green-700">
                  {editing ? "Salvar" : "Criar Promoção"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation (substitui window.confirm). */}
      {pendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setPendingDelete(null)}>
          <div className="bg-card rounded-xl border shadow-2xl w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
            <Trash2 className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold mb-2">Excluir promoção?</h3>
            <p className="text-sm text-muted-foreground mb-1">
              {pendingDelete.name ? <strong>{pendingDelete.name}</strong> : "Esta promoção"} será removida.
            </p>
            <p className="text-xs text-muted-foreground mb-5">Não dá pra desfazer.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setPendingDelete(null)}>
                Cancelar
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={confirmDelete}>
                Excluir
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Discard confirmation */}
      {showDiscardConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowDiscardConfirm(false)}>
          <div className="bg-card rounded-xl border shadow-2xl w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
            <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold mb-2">Descartar alterações?</h3>
            <p className="text-sm text-muted-foreground mb-5">Tem certeza que deseja descartar as alterações não salvas?</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowDiscardConfirm(false)}>
                Continuar Editando
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={confirmDiscard}>
                Descartar
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ============================================================
// CashbackConfigModal — configuração de cashback (v1.5.284+)
// Backend: GET/PUT /api/cashback/config → system_settings.cashback_config
// ============================================================
/** Quem ganha cashback (tipos da tela de Clientes) e, se quiser, um % próprio
 *  para cada tipo — vazio = o % geral. Quem não ganha continua podendo USAR o
 *  saldo que já tem. */
function CashbackPorTipo({ config, setConfig }) {
  const segs = config.segmentos || [];
  const porTipo = config.por_segmento || {};
  const [diferente, setDiferente] = useState(Object.keys(porTipo).length > 0);
  useEffect(() => { if (Object.keys(config.por_segmento || {}).length) setDiferente(true); }, [config.por_segmento]);
  const tipos = segs.length ? segs : ORDEM_TIPOS;
  // O texto digitado fica aqui ("7," no meio da digitação); o número vai para a config
  const [textos, setTextos] = useState({});
  const mudarPct = (k, texto) => {
    setTextos((t) => ({ ...t, [k]: texto }));
    const n = parseFloat(sanitizeCurrencyInput(texto));
    setConfig((c) => {
      const novo = { ...(c.por_segmento || {}) };
      if (isNaN(n) || n <= 0) delete novo[k]; else novo[k] = n;
      return { ...c, por_segmento: novo };
    });
  };
  return (
    <div className="rounded-xl border p-4 space-y-3" data-e2e="cashback-por-tipo">
      <ParaQuemVale titulo="Quem ganha cashback" e2e="cashback-quem-ganha" valor={segs}
        aoMudar={(v) => setConfig((c) => ({ ...c, segmentos: v }))}
        ajuda="Só os tipos marcados acumulam crédito. Quem não está neles continua podendo usar o saldo que já tem." />
      <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer pt-1">
        <Switch checked={diferente} onCheckedChange={(v) => {
          setDiferente(v);
          if (!v) { setConfig((c) => ({ ...c, por_segmento: {} })); setTextos({}); }
        }} data-e2e="cashback-pct-por-tipo" />
        Percentual diferente para cada tipo
      </label>
      {diferente && (
        <div className="grid sm:grid-cols-2 gap-2">
          {tipos.map((k) => (
            <div key={k} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2">
              <span className="text-sm min-w-0">
                <span className="font-medium">{TIPOS_CLIENTE[k].nome}</span>
                <span className="block text-[11px] text-muted-foreground truncate">{TIPOS_CLIENTE[k].regra}</span>
              </span>
              <div className="relative shrink-0">
                <input type="text" inputMode="decimal" data-e2e={`cashback-pct-${k}`}
                  value={textos[k] ?? (porTipo[k] ? String(porTipo[k]).replace(".", ",") : "")}
                  onChange={(e) => mudarPct(k, e.target.value)}
                  placeholder={String(config.percentual || 0).replace(".", ",")}
                  className="w-20 h-9 pl-2 pr-6 text-right text-sm font-bold tabular-nums rounded-md border bg-card focus:outline-none focus:border-primary" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">%</span>
              </div>
            </div>
          ))}
          <p className="sm:col-span-2 text-[11px] text-muted-foreground">Vazio = o percentual geral ({String(config.percentual || 0).replace(".", ",")}%).</p>
        </div>
      )}
    </div>
  );
}

export function CashbackConfigModal({ open, onOpenChange, config, setConfig, loading, setLoading, saving, setSaving }) {
  // Fetch da config ao abrir. Zero polling — só na abertura.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem("token");
    axios.get(`${API}/cashback/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled || !res.data) return;
        setConfig((c) => ({
          ativo: !!res.data.ativo,
          percentual: Number(res.data.percentual || 0),
          modulos: Array.isArray(res.data.modulos) ? res.data.modulos : ["Cardapio"],
          // Vindo do botão de ofertas da tela de Clientes, o tipo já chega marcado
          segmentos: c.preSegmento ? [c.preSegmento] : (res.data.segmentos || []),
          por_segmento: res.data.por_segmento || {},
        }));
      })
      .catch((err) => {
        if (!cancelled) console.error("[Cashback] fetch config:", err);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    if (config.ativo && (!config.percentual || config.percentual <= 0)) {
      toast.error("Digite um percentual maior que 0 antes de ativar.");
      return;
    }
    if (config.percentual > 50) {
      toast.error("Percentual muito alto (máximo 50%).");
      return;
    }
    const modulos = config.modulos || ["Cardapio"];
    if (config.ativo && modulos.length === 0) {
      toast.error("Habilite pelo menos um módulo para o cashback.");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/cashback/config`,
        {
          ativo: !!config.ativo, percentual: Number(config.percentual || 0), modulos,
          segmentos: config.segmentos || [],
          // % por tipo vazio = o geral (não manda)
          por_segmento: Object.fromEntries(Object.entries(config.por_segmento || {}).filter(([, v]) => Number(v) > 0)),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        config.ativo
          ? `Cashback ativado com ${config.percentual}% de retorno.`
          : "Cashback desativado.",
      );
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Erro ao salvar configuração";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header gradient */}
        <DialogHeader className="p-0 space-y-0">
          <div className="bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground px-6 pt-6 pb-14">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                <Coins className="w-6 h-6" />
              </div>
              <DialogTitle asChild>
                <h2 className="text-2xl font-black leading-tight">Cashback</h2>
              </DialogTitle>
            </div>
            <p className="text-sm text-primary-foreground/85 max-w-md">
              Devolva uma porcentagem em crédito pro cliente em cada compra.
              O saldo acumula pra próxima ida ao seu estabelecimento.
            </p>
          </div>
          {/* "Onda" separando header do conteúdo */}
          <div className="-mt-6 h-6 bg-background rounded-t-3xl" />
        </DialogHeader>

        {/* Corpo */}
        <div className="flex-1 overflow-y-auto px-6 pt-2 pb-4 space-y-5">
          {loading ? (
            <CarregandoFormulario campos={3} className="space-y-5 py-2" />
          ) : (
            <>
              {/* Switch ativar */}
              <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
                <div className="min-w-0">
                  <Label htmlFor="cb-ativo" className="text-sm font-bold flex items-center gap-2 cursor-pointer">
                    <Coins className="w-4 h-4 text-primary" />
                    Ativar Cashback
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    Quando ligado, cada pedido gera crédito automático pro cliente com o percentual definido abaixo.
                  </p>
                </div>
                <Switch
                  id="cb-ativo"
                  checked={config.ativo}
                  onCheckedChange={(v) => setConfig((c) => ({ ...c, ativo: v }))}
                />
              </div>

              {/* Percentual */}
              <div className="rounded-xl border p-4 space-y-3">
                <div>
                  <Label htmlFor="cb-pct" className="text-sm font-bold flex items-center gap-2">
                    <Percent className="w-4 h-4 text-primary" />
                    Porcentagem de retorno
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ex: <strong>5%</strong> significa que a cada R$ 100 em compras, o cliente ganha R$ 5 de crédito.
                  </p>
                </div>
                <div className="relative">
                  <input
                    id="cb-pct"
                    type="text"
                    inputMode="decimal"
                    value={config.percentual === 0 ? "" : String(config.percentual)}
                    onChange={(e) => {
                      const clean = sanitizeCurrencyInput(e.target.value);
                      const n = parseFloat(clean);
                      setConfig((c) => ({ ...c, percentual: isNaN(n) ? 0 : n }));
                    }}
                    placeholder="0"
                    className="w-full pl-4 pr-12 h-14 text-2xl font-black tabular-nums rounded-lg border-2 border-primary/30 bg-card focus:outline-none focus:border-primary transition-colors"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xl font-black text-primary">%</span>
                </div>
                {/* Simulação — conta base em R$ 1,00 pra ficar didático */}
                {config.percentual > 0 && config.percentual <= 50 && (
                  <div className="rounded-lg bg-primary/[0.06] border border-primary/20 p-3 text-xs space-y-1">
                    <p className="font-bold text-primary flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5" />
                      Como funciona
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      A cada <strong className="text-foreground">R$ 1,00</strong> em compras, o cliente ganha{" "}
                      <strong className="text-primary">R$ {(config.percentual / 100).toFixed(2).replace('.', ',')}</strong> de cashback.
                    </p>
                  </div>
                )}
                {config.percentual > 50 && (
                  <p className="text-xs text-red-600 dark:text-red-400 font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Máximo permitido: 50% (evita erro de digitação).
                  </p>
                )}
              </div>

              {/* Quem ganha e % por tipo de cliente (dono, 27/10/2026) */}
              <CashbackPorTipo config={config} setConfig={setConfig} />

              {/* Módulos onde o cashback vale (ganhar e usar). Default: só Cardápio */}
              <div className="rounded-xl border p-4 space-y-3">
                <div>
                  <Label className="text-sm font-bold flex items-center gap-2">
                    <Coins className="w-4 h-4 text-primary" />
                    Módulos com cashback
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Escolha onde o cliente ganha e pode usar o cashback.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "Cardapio", label: "Cardápio" },
                    { key: "Delivery", label: "Delivery" },
                    { key: "Mesas", label: "Mesas" },
                    { key: "MesaQR", label: "Mesa QR" },
                    { key: "Comandas", label: "Comandas" },
                    { key: "Balcao", label: "Balcão" },
                    { key: "Totem", label: "Totem" },
                    // Pedido anotado pela CLEO na conversa. Sem marcar, ele não gera cashback.
                    { key: "ChatBot", label: "WhatsApp" },
                  ].map((m) => {
                    const marcado = (config.modulos || ["Cardapio"]).includes(m.key);
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() =>
                          setConfig((c) => {
                            const atual = c.modulos || ["Cardapio"];
                            return {
                              ...c,
                              modulos: atual.includes(m.key)
                                ? atual.filter((x) => x !== m.key)
                                : [...atual, m.key],
                            };
                          })
                        }
                        className={`flex items-center justify-between gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-semibold transition-colors ${
                          marcado
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        {m.label}
                        <span
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            marcado ? "border-primary bg-primary" : "border-muted-foreground/40"
                          }`}
                        >
                          {marcado && <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {(config.modulos || []).includes("ChatBot") && (
                  <p className="text-xs text-muted-foreground" data-e2e="cashback-aviso-whatsapp">
                    No WhatsApp o cliente só ganha o cashback, no pedido anotado pela CLEO. Para usar o saldo, ele pede pelo cardápio.
                  </p>
                )}
                {config.ativo && (config.modulos || []).length === 0 && (
                  <p className="text-xs text-red-600 dark:text-red-400 font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Habilite pelo menos um módulo.
                  </p>
                )}
              </div>

            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-muted/30 px-6 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="w-full sm:w-auto">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================ */
/* Sub-componentes de linha de desconto — pizza / combo / simples */
/* ============================================================ */

/** Mostra "R$ X → R$ Y" inline. Compacto, usado nas linhas de desconto.
 * Quando o preço final é null (sem desconto ou inválido) mostra só "—". */
/** Step indicator do wizard. Clica numa step pra ir direto pra ela
 * (valida steps anteriores antes de avançar). */
function WizardSteps({ current, onGo }) {
  return (
    <div className="px-5 py-3 border-b bg-muted/20 shrink-0">
      <div className="flex items-center gap-2">
        {WIZARD_STEPS.map((s, idx) => {
          const isCurrent = idx === current;
          const isPast = idx < current;
          return (
            <div key={s.key} className="flex items-center gap-2 flex-1">
              <button
                type="button"
                onClick={() => onGo(idx)}
                className={[
                  "flex items-center gap-2 transition-colors",
                  isCurrent
                    ? "text-green-600 dark:text-green-400"
                    : isPast
                    ? "text-foreground hover:text-green-600"
                    : "text-muted-foreground",
                ].join(" ")}
              >
                <span className={[
                  "w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors",
                  isCurrent
                    ? "bg-green-500 text-white"
                    : isPast
                    ? "bg-green-500/20 text-green-700 dark:text-green-300"
                    : "bg-muted text-muted-foreground",
                ].join(" ")}>
                  {idx + 1}
                </span>
                <span className="text-xs font-semibold hidden sm:inline">{s.label}</span>
              </button>
              {idx < WIZARD_STEPS.length - 1 && (
                <div className={`h-px flex-1 ${idx < current ? "bg-green-500/40" : "bg-border"}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Campo "Limite de unidades" no Step 3. Input + barra de progresso quando
 * editando (mostra X / Y vendidas). */
function UsageLimitField({ editing, value, onChange }) {
  const used = editing?.usage_count || 0;
  const limit = parseInt(value || "0", 10);
  const hasLimit = limit > 0;
  const pct = hasLimit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div>
      <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
        <TrendingUp className="w-3.5 h-3.5 text-green-500" />
        Limite de unidades vendidas
      </label>
      <p className="text-[11px] text-muted-foreground mb-2 leading-snug">
        Define quantas unidades dos produtos em promoção podem ser vendidas no total.
        Quando atinge o limite, a promoção desativa automaticamente.
        Deixe em <strong>0</strong> ou vazio para promoção ilimitada.
      </p>
      <input
        type="number"
        min="0"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ex: 30 unidades — deixe vazio pra ilimitada"
        className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50"
      />
      {editing && hasLimit && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
            <span>Vendidas</span>
            <span className="font-bold tabular-nums text-foreground">
              {used} / {limit}
            </span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={[
                "h-full rounded-full transition-all",
                pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-green-500",
              ].join(" ")}
              style={{ width: `${pct}%` }}
            />
          </div>
          {pct >= 100 && (
            <p className="text-[11px] text-red-600 dark:text-red-400 mt-1 font-medium">
              Promoção esgotada — o preço já voltou ao normal.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function PriceArrow({ base, final }) {
  const hasFinal = Number.isFinite(Number(final));
  if (!Number.isFinite(Number(base)) || Number(base) <= 0) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  if (!hasFinal) {
    return (
      <span className="text-[10px] text-muted-foreground tabular-nums">
        {fmtBRL(base)}
      </span>
    );
  }
  return (
    <span className="text-[10px] tabular-nums inline-flex items-center gap-1">
      <span className="text-muted-foreground line-through">{fmtBRL(base)}</span>
      <span className="text-green-600 dark:text-green-400 font-bold">→ {fmtBRL(final)}</span>
    </span>
  );
}

/** Linha de um tamanho dentro de uma pizza. */
function PizzaSizeRow({ sizeName, basePrice, finalPrice, hasInput, discountPrefix, discountType, value, onChange }) {
  if (CampoDesconto.atende(discountType)) {
    return (
      <div className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/20 transition-colors">
        <span className="text-[11px] font-medium text-muted-foreground w-20 truncate">{sizeName}</span>
        <div className="ml-auto"><CampoDesconto tipo={discountType} base={basePrice} value={value} onChange={onChange} compacto /></div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/20 transition-colors">
      <span className="text-[11px] font-medium text-muted-foreground w-20 truncate">
        {sizeName}
      </span>
      <div className="flex items-center gap-1 ml-auto">
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="—"
          className="w-16 px-2 py-1 rounded border bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500/50"
        />
        <span className="text-[10px] text-muted-foreground font-medium w-4">{discountPrefix}</span>
      </div>
      <div className="w-32 text-right">
        <PriceArrow base={basePrice} final={hasInput ? finalPrice : null} />
      </div>
    </div>
  );
}

/** Linha de desconto pra combo (2 versões: simples e combo). */
function ComboDiscountRow({ label, color, discountPrefix, value, onChange, basePrice, discountType }) {
  const final = calcFinalPrice(basePrice, discountType, value);
  if (CampoDesconto.atende(discountType)) {
    return (
      <div className="rounded-lg border bg-background/40 p-2 flex flex-col gap-1">
        <span className={`text-[10px] font-bold ${color}`}>{label}</span>
        <CampoDesconto tipo={discountType} base={basePrice} value={value} onChange={onChange} compacto />
      </div>
    );
  }
  return (
    <div className="rounded-lg border bg-background/40 p-2 flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <span className={`text-[10px] font-bold whitespace-nowrap ${color}`}>{label} {discountPrefix}</span>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Valor"
          className="flex-1 min-w-0 px-2 py-1 rounded border bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500/50"
        />
      </div>
      <PriceArrow base={basePrice} final={final} />
    </div>
  );
}

/** Linha de desconto pra produto simples (não-combo, não-pizza). */
function SimpleDiscountRow({ discountPrefix, value, onChange, basePrice, discountType }) {
  const final = calcFinalPrice(basePrice, discountType, value);
  if (CampoDesconto.atende(discountType)) {
    return <div className="mt-2"><CampoDesconto tipo={discountType} base={basePrice} value={value} onChange={onChange} /></div>;
  }
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      <span className="text-[10px] text-muted-foreground font-medium">{discountPrefix}</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Valor"
        className="w-20 px-2 py-1 rounded border bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500/50"
      />
      <PriceArrow base={basePrice} final={final} />
    </div>
  );
}

